// ** Modules
import React from 'react';

// ** Components
import VerticalNavLink from './VerticalNavLink';
import VerticalNavGroup from './VerticalNavGroup';
import VerticalNavSectionTitle from './VerticalNavSectionTitle';

// ** Types
import type { NavLink, NavGroup, NavSectionTitle, LayoutProps } from '@/types/layoutProps';

type VerticalLayout = NonNullable<LayoutProps['verticalLayoutProps']>;
type NavMenu = NonNullable<VerticalLayout['navMenu']>;
type NavItemsProps = {
	parent?: NavGroup;
	navHover?: boolean;
	navVisible?: boolean;
	groupActive: string[];
	isSubToSub?: NavGroup;
	currentActiveGroup: string[];
	navigationBorderWidth: number;
	settings: LayoutProps['settings'];
	saveSettings: LayoutProps['saveSettings'];
	setGroupActive: (value: string[]) => void;
	setCurrentActiveGroup: (item: string[]) => void;
	verticalNavItems?: NavMenu['navItems'];
	nestedlevel?: number;
};

// Type guard for NavSectionTitle
const isNavSectionTitle = (item: any): item is NavSectionTitle => {
	// return (item as NavSectionTitle).sectionTitle !== undefined;
	return (item as NavSectionTitle).__typename === 'ComponentNavigationMenuDivider';
};

// Type guard for NavGroup
const isNavGroup = (item: any): item is NavGroup => {
	// return (item as NavGroup).children !== undefined;
	return !!item && Array.isArray(item.children) && item.children.length > 0;
};

// Type guard for NavLink
const isNavLink = (item: any): item is NavLink => {
	return (item as NavLink)?.children?.length === 0 || (item as NavLink).children === undefined;
};

// Helper function to check if a NavGroup has a parent NavGroup
const hasParentNavGroup = (parent?: NavGroup): boolean => {
	if (!parent) return false;
	return isNavGroup(parent);
};

const resolveNavItemComponent = (item: NavGroup | NavLink | NavSectionTitle) => {
	if (isNavSectionTitle(item)) return VerticalNavSectionTitle;
	if (isNavGroup(item)) return VerticalNavGroup;

	return VerticalNavLink;
};

// --- Helper to generate stable unique keys ---
const generateKey = (item: NavGroup | NavLink | NavSectionTitle, index: number): string => {
	// Prefer explicit identifiers first
	if ('id' in item && item.id) return String(item.id);
	if ('slug' in item && item.slug) return String(item.slug);
	if ('title' in item && item.title) return String(item.title);
	if ('sectionTitle' in item && item.sectionTitle) return String(item.sectionTitle);

	// Fallback deterministic key based on type + index + content
	const type = (item as any).__typename || 'NavItem';
	const extra = JSON.stringify(item).replace(/\W+/g, '').slice(0, 20) || 'no-content';
	return `${type}-${extra}-${index}`;
};

const VerticalNavItems = (props: NavItemsProps) => {
	// Vars
	const { verticalNavItems, nestedlevel = 0 } = props;

	const RenderMenuItems = verticalNavItems?.map((item: NavLink | NavSectionTitle | NavGroup, index) => {
		const TagName: any = resolveNavItemComponent(item);

		// Check if the current item is a NavGroup with a parent NavGroup
		const isNestedGroup = hasParentNavGroup(props.parent);
		const currentnestedlevel = isNestedGroup ? nestedlevel + 1 : nestedlevel;

		const newProps =
			isNavGroup(item) && item.children && item.children.length > 0 && props.parent
				? { ...props, isSubToSub: item, nestedlevel: currentnestedlevel }
				: isNavLink(item)
					? { ...props, isSubToSub: item, nestedlevel: currentnestedlevel }
					: props;

		const key = generateKey(item, index);

		return <TagName {...newProps} key={key} item={item} />;
	});

	return <React.Fragment>{RenderMenuItems}</React.Fragment>;
};

export default VerticalNavItems;
